// 등급 요약 「다시 읽어라」 신호 — 채널 쓰기 도우미(channelWrites)·탭 복귀가 보내고, 등급 요약 훅·심사 상태 훅·내 등급 화면이 듣는다.
// 예전에는 이 신호가 등급 요약 훅 파일 안에 있어 lib(channelWrites)가 React 훅 모듈을 끌어왔다(리뷰 93차 #5). 신호는 여기 한 곳, 캐시는 그 캐시를 가진 모듈이
// onTierRowsReset 으로 「신호 때 비우기」를 등록하고, 탭 복귀 때 다시 읽을지는 addTierRowsRevisitCheck 로 등록한 판정(캐시를 가진 모듈마다)이 정한다.
// 두 갈래: ① 전체 신호(invalidateTierRows — 채널 쓰기) = 모든 캐시를 비우고 모두에게 알린다 ② 탭 복귀 = **오래된 캐시를 가진 모듈만** 그 모듈 캐시를
// 비우고 그 모듈을 듣는 쪽에만 알린다 — 하나가 오래됐다고 방금 읽은 다른 캐시까지 비우면 탭을 오갈 때마다 불필요한 조회가 난다(102차 #5)
let generation = 0;   // 캐시 유효 세대 — 전체 신호만 올린다. 조회를 시작할 때 적어 두고, 끝났을 때 바뀌었으면 그 결과를 캐시에 쓰지 않는다(64차 #1)
let seq = 0;          // 알림 번호 — 전체 신호·모듈 다시 읽기 모두 올린다(듣는 쪽 상태가 매번 새 값이 되게). generation 보다 작아지지 않는다
const listeners = new Set();
const resetters = new Set();
const revisits = new Set();

/** 지금 세대 번호 — 조회를 시작할 때 적어 두고, 끝났을 때 바뀌었으면 그 결과를 캐시에 쓰지 않는다(64차 #1) */
export const tierRowsGeneration = () => generation;

/** 전체 신호 때 먼저 비울 캐시 등록(구독자에게 알리기 전에 비운다 — 구독자가 다시 읽을 때 옛 캐시를 보지 않게) */
export function onTierRowsReset(fn) {
  resetters.add(fn);
  return () => resetters.delete(fn);
}

/**
 * 탭이 다시 보일 때 — 캐시를 가진 모듈마다 판정(check)과 그 모듈만 다시 읽게 하는 함수(refresh)를 하나씩 등록한다(89차 #2 · 90차 #2·#3 · 91차 #1 · 94차 #4).
 * 판정이 참인 모듈의 refresh 만 부른다(102차 #5). 돌려준 함수로 뗀다
 */
export function addTierRowsRevisitCheck(check, refresh) {
  const entry = { check, refresh };
  revisits.add(entry);
  return () => revisits.delete(entry);
}

function onVisible() {
  if (typeof document === "undefined" || document.visibilityState !== "visible") return;
  // 판정을 먼저 다 본 뒤 다시 읽는다 — 앞 모듈의 다시 읽기가 뒤 모듈의 판정(진행 중 요청 등)을 바꾸지 않게
  const stale = [...revisits].filter((r) => r.check());
  for (const r of stale) r.refresh();
}

/** 신호 듣기 — 듣는 쪽이 하나라도 있을 때만 탭 복귀를 듣는다. 돌려준 함수로 그만 듣는다 */
export function subscribeTierRows(listener) {
  const entry = (v) => listener(v);   // 같은 함수를 두 번 등록해도 하나를 뗄 때 다른 하나가 같이 빠지지 않게
  if (!listeners.size && typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
  listeners.add(entry);
  return () => {
    listeners.delete(entry);
    if (!listeners.size && typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
  };
}

/** 다시 읽어라(전체) — 세대를 올리고, 모든 캐시를 비우고, 듣는 쪽 모두에 새 번호를 알린다 */
export function invalidateTierRows() {
  generation += 1;
  seq += 1;
  const v = seq;
  for (const reset of [...resetters]) reset();
  for (const listener of [...listeners]) listener(v);
}

/** 탭 복귀 때 「오래됐다」로 보는 나이 — 두 캐시(등급 요약 · 심사 상태)가 같은 값을 쓴다(96차 #4) */
export const TIER_REVISIT_MS = 30 * 1000;

/**
 * 회원마다 「마지막으로 서버에 물은 시각」을 적는다(실패 뒤 다시 시도 간격 · 91차 #1 · 102차 #4). 적을 때 지금 그리지 않는 회원 중
 * 30초가 지난 칸을 지운다 — 그 칸은 더는 간격을 막지 않는다(대리 접속으로 여러 회원을 오가도 칸이 쌓이지 않게 · 114차 #5)
 */
export function noteTierAttempt(attempts, watch, email, now = Date.now()) {
  for (const [e, t] of attempts) if (e !== email && !watch.isActive(e) && now - t >= TIER_REVISIT_MS) attempts.delete(e);
  attempts.set(email, now);
}

/**
 * 캐시를 가진 모듈마다 하나 — 「지금 이 회원을 그리는 켜진 훅」 수를 세고 신호를 듣게 한다(96차 #4 · 두 훅에 복사돼 있던 것을 한 곳으로).
 * 모듈마다 따로 만드는 까닭: 등급 요약 훅은 정식 등급 회원에게도 켜지지만 심사 상태 훅은 루키 계열에만 켜진다 — 같은 수를 쓰면 정식 등급 회원의
 * 남은 심사 상태 캐시가 탭 복귀마다 신호를 보낸다(95차 #1)
 *   watch(email, listener) → 그만 듣는 함수 · subscribe(listener, email?) → 회원 수는 세지 않고 이 모듈 알림·전체 신호를 듣기 ·
 *   notify(emails?) → 이 모듈을 듣는 쪽 중 그 회원(들)을 그리는 쪽에만 새 번호(탭 복귀 다시 읽기 · 102차 #5 — 회원을 적지 않고 들은 쪽은 늘 받는다).
 *   회원으로 거르는 까닭: 오래된 회원 하나 때문에 다른 회원(대리 접속 창 둘)의 훅까지 다시 그리면, 그 회원의 지난 조회가 실패해 캐시가 없을 때
 *   「마지막으로 물은 지 30초」 간격을 건너뛰고 다시 묻는다(102차 #4) · isActive(email) · hasAny() · emails()
 */
export function createTierRowsWatch() {
  const active = new Map();
  const local = new Set();
  const listen = (listener, email) => {
    const entry = { email, fn: (v) => listener(v) };
    local.add(entry);
    const off = subscribeTierRows(listener);
    return () => {
      off();
      local.delete(entry);
    };
  };
  const watch = {
    watch(email, listener) {
      active.set(email, (active.get(email) || 0) + 1);
      const off = listen(listener, email);
      return () => {
        off();
        const n = (active.get(email) || 1) - 1;
        if (n > 0) active.set(email, n);
        else active.delete(email);
      };
    },
    subscribe: listen,
    notify(emails) {
      seq += 1;
      const v = seq;
      const only = emails ? new Set(emails) : null;
      for (const e of [...local]) if (!only || e.email === undefined || e.email === null || only.has(e.email)) e.fn(v);
    },
    isActive: (email) => active.has(email),
    hasAny: () => active.size > 0,
    emails: () => [...active.keys()],
  };
  return watch;
}

/**
 * 탭 복귀 다시 읽기 규칙 한 곳(104차 #5 — 두 캐시 모듈에 거의 같은 판정이 복사돼 고칠 때마다 두 벌을 같이 고쳤다).
 * 지금 그리는 회원마다(대리 접속 전환 · 99차 #5 · 100차 #1 · 102차 #4):
 *   그 회원 요청이 진행 중 → 기다린다 · 그 회원 캐시가 있으면 → revisitable(e) 이고 30초보다 오래됐을 때 ·
 *   캐시가 없으면(지난 조회 실패 · 다른 회원 응답이 밀어냄) → 그 회원을 마지막으로 물은 지 30초가 지났을 때(장애 중 탭을 오갈 때마다 부르지 않게 · 91차 #1).
 *   한 번도 물은 적이 없으면 아니다 — 첫 조회는 훅이 한다(102차 #7)
 * 판정이 참인 회원의 캐시만 dropCache(e) 로 비우고 그 회원을 그리는 쪽에만 알린다(102차 #5).
 *   모듈이 주는 것: isInflight(e) · cacheAt(e)(없으면 undefined) · revisitable(e)(없으면 늘 참) · lastAttemptAt(e) · dropCache(e)
 * 돌려준 함수로 뗀다
 */
export function addTierRowsRevisit(watch, { isInflight, cacheAt, revisitable = () => true, lastAttemptAt, dropCache }) {
  const stale = () => {
    const now = Date.now();
    return watch.emails().filter((e) => {
      if (isInflight(e)) return false;
      const at = cacheAt(e);
      if (at !== undefined && at !== null) return revisitable(e) && now - at > TIER_REVISIT_MS;
      const t = lastAttemptAt(e);
      return t !== undefined && t !== null && now - t > TIER_REVISIT_MS;
    });
  };
  return addTierRowsRevisitCheck(() => stale().length > 0, () => {
    const emails = stale();
    for (const e of emails) dropCache(e);
    watch.notify(emails);
  });
}
